/**
 * @file Inline Object Editor for grid rows (outputs flat children)
 * @version 1.0.0
 */

import ObjectEditor from './ObjectEditor.js';
import { CONTROLS_TEMPLATE } from './PrimitiveEditor.js';
import CompactPrimitiveEditor from './CompactPrimitiveEditor.js';
import { registerEditor, resolveEditor } from './component-registry.js';

const InlineObjectEditor = {
  name: 'InlineObjectEditor',
  extends: ObjectEditor,

  setup(props, ctx) {
    return ObjectEditor.setup(props, ctx);
  },

  methods: {
    /**
     * Override to use inline/compact variants for grid display
     * @param fieldSchema
     */
    getItemComponent(fieldSchema) {
      // Use explicit editor if specified
      if (fieldSchema.editor) {
        return fieldSchema.editor;
      }
      // Use CompactPrimitiveEditor for primitives (not in registry)
      if (fieldSchema.type !== 'object' && fieldSchema.type !== 'array') {
        return CompactPrimitiveEditor;
      }
      // Force inline variant for objects, grid for arrays
      const variant = fieldSchema.type === 'object' ? 'inline' : 'grid';
      return resolveEditor({ ...fieldSchema, variant }, this.mergedDefaults);
    },
  },

  template: `
    <template v-if="canRead && !isUndefined">
      <!-- Controls as first grid cell -->
      <div class="d-flex gap-1 align-items-center" :style="controlStyles">
        ${CONTROLS_TEMPLATE}
      </div>

      <!-- Each field as a grid cell -->
      <template v-for="fieldName in fieldNames" :key="fieldName">
        <component
          :is="getItemComponent(getFieldSchema(fieldName))"
          :schema="getFieldSchema(fieldName)"
          :value="getFieldValue(fieldName)"
          :reference="getFieldReference(fieldName)"
          :mode="childMode"
          :deletable="false"
          :displayLabel="false"
          @input="(state) => handleFieldInput(fieldName, state)"
        />
      </template>
    </template>

    <!-- Undefined state - show placeholder cells -->
    <template v-else-if="canRead && isUndefined">
      <div class="d-flex gap-1 align-items-center" :style="controlStyles">
        ${CONTROLS_TEMPLATE}
      </div>
      <template v-for="fieldName in fieldNames" :key="fieldName">
        <span class="text-muted fst-italic">—</span>
      </template>
    </template>
  `,
};

registerEditor('object', 'inline', InlineObjectEditor);

export default InlineObjectEditor;
